/**
 * Karbon360° v2 — design-system gallery.
 *
 * Not in the navigation; reachable at /ui-kit. Two jobs: it verifies every
 * primitive renders correctly after a change, and it is the reference Phase 3+
 * screens are built from. Static by design — this is the component catalogue,
 * not a data screen.
 */
import React, { useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import {
  Button, Card, PageHeader, Tabs, Badge, DataQuality, Delta, Stat,
  Field, Input, Select, UnitInput, SearchInput, DList, Sect, CalcTrace,
  Callout, Empty, Skeleton, Drawer, Modal, Stepper, BarRow,
  TableWrap, SortHeader, useToast, fmt,
} from '../components/v2/ui';

function Row({ title, children }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.075em', textTransform: 'uppercase', color: 'var(--n500)', marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>{children}</div>
    </div>
  );
}

function Gallery() {
  const { toast } = useToast();
  const [tab, setTab] = useState('all');
  const [drawer, setDrawer] = useState(false);
  const [modal, setModal] = useState(false);
  const [sort, setSort] = useState({ key: 'co2', dir: -1 });
  const [q, setQ] = useState('');

  const rows = [
    { id: 'INV-4192', doc: 'KESCO · Aug 2025', site: 'Habitat HQ', kwh: 292, co2: 0.3941, dq: 3, src: 'OCR' },
    { id: 'INV-4185', doc: 'Termokos · Jan 2026', site: 'Main Office', kwh: 4120, co2: 0.8693, dq: 3, src: 'Manual' },
    { id: 'INV-4170', doc: 'Air travel · Nov 2025', site: 'Habitat HQ', kwh: null, co2: 0.4120, dq: 1, src: 'Manual' },
  ];

  return (
    <div className="k2-wrap">
      <PageHeader
        title="Design system"
        desc="Every v2 primitive, in one place. Screens from Phase 3 onward are assembled from these rather than inline styles."
      >
        <Button icon={X.Doc} onClick={() => toast('This is a toast')}>Toast</Button>
        <Button variant="mint" icon={X.Check} onClick={() => toast('Saved successfully')}>Success toast</Button>
      </PageHeader>

      <Row title="Buttons">
        <Button>Default</Button>
        <Button variant="pri">Primary</Button>
        <Button variant="mint" icon={X.Check}>Mint</Button>
        <Button variant="gh">Ghost</Button>
        <Button variant="dgr" icon={X.Close}>Danger</Button>
        <Button size="sm">Small</Button>
        <Button iconOnly icon={X.Gear} aria-label="Settings" />
        <Button disabled>Disabled</Button>
      </Row>

      <Row title="Badges & data quality">
        <Badge>Neutral</Badge>
        <Badge tone="ok">Posted</Badge>
        <Badge tone="warn" dot>Needs review</Badge>
        <Badge tone="err">Failed</Badge>
        <Badge tone="mint">OCR</Badge>
        <Badge tone="out">Scope 2</Badge>
        <DataQuality q={3} />
        <DataQuality q={2} />
        <DataQuality q={1} />
        <Delta v={-6.4} />
        <Delta v={12.1} />
        <Delta v={0.01} />
      </Row>

      <div className="k2-g4" style={{ marginBottom: 26 }}>
        <Stat tone="dark" label="Total footprint" value={fmt(1924.72)} unit="tCO₂e" delta={-6.4} foot="vs prior year" />
        <Stat label="Intensity" value="253" unit="kgCO₂e/m²" delta={3.1} foot="16,050 m²" />
        <Stat label="Inventory completeness" value="7" unit="/ 12 months" foot="3 complete · 4 partial" />
        <Stat tone="mint" label="Data quality score" value="89" unit="%" delta={4.2} invert foot="7% still estimated" />
      </div>

      <Tabs value={tab} onChange={setTab} items={[
        { id: 'all', label: 'All', count: 3 },
        { id: 'review', label: 'Needs review', count: 1 },
        { id: 's1', label: 'Scope 1' },
      ]} />

      <div className="k2-g64" style={{ marginBottom: 26 }}>
        <Card title="Table" sub="Sortable headers, sticky head, hover row actions">
          <div className="k2-fb">
            <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search document or site…" />
          </div>
          <TableWrap
            toolbar={<span style={{ fontSize: 12.5, color: 'var(--n500)' }}>{rows.length} records</span>}
            footer={<span className="k2-num">Showing {rows.length} of {rows.length}</span>}
          >
            <table className="k2-t">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Quality</th>
                  <th>Source</th>
                  <SortHeader id="co2" label="tCO₂e" sort={sort} setSort={setSort} right />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="k2-cellflex">
                        <span className="k2-tsq k2-mint"><X.Doc s={14} /></span>
                        <span style={{ minWidth: 0 }}>
                          <span className="k2-nm">{r.doc}</span>
                          <span className="k2-sub2 k2-mono">{r.id}</span>
                        </span>
                      </div>
                    </td>
                    <td><DataQuality q={r.dq} showLabel={false} /></td>
                    <td><Badge tone={r.src === 'OCR' ? 'mint' : undefined}>{r.src}</Badge></td>
                    <td className="k2-r k2-num" style={{ fontWeight: 600 }}>{fmt(r.co2, 3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>

        <div className="k2-gcol">
          <Card title="Form fields">
            <Field label="Site" required hint="Records must attach to a site so intensity stays correct.">
              <Select defaultValue=""><option value="">Select a site…</option><option>Habitat HQ</option></Select>
            </Field>
            <Field label="Consumption" required>
              <UnitInput unit="kWh" placeholder="292" />
            </Field>
            <Field label="Meter ID" optional error="This meter is already registered for July.">
              <Input invalid defaultValue="67262184" />
            </Field>
          </Card>

          <Card title="Coverage">
            <BarRow name="Measured" value="11.99" unit="t" pct={84} color="var(--ok)" />
            <BarRow name="Calculated" value="1.25" unit="t" pct={9} color="var(--brand-700)" />
            <BarRow name="Estimated" value="1.05" unit="t" pct={7} color="var(--warn)" />
          </Card>
        </div>
      </div>

      <Sect title="Calculation trace — self-checking">
        <div className="k2-g2" style={{ marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--n500)', marginBottom: 6 }}>Reconciles correctly</div>
            <CalcTrace activity={292} activityUnit="kWh" factor={1.34} factorUnit="kgCO₂e/kWh" result={0.39128} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--n500)', marginBottom: 6 }}>
              Does not reconcile — the prototype rendered this silently
            </div>
            <CalcTrace activity={9800} activityUnit="kWh" factor={0.202} factorUnit="kgCO₂e/kWh" result={1.96} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: 'var(--n500)', marginBottom: 6 }}>
            Tonne-based process factor — divisor 1000 would be wrong here
          </div>
          <CalcTrace activity={2.7} activityUnit="t" factor={525} factorUnit="kgCO₂/t" result={1.4175} />
        </div>
      </Sect>

      <Sect title="Callouts">
        <div className="k2-gcol">
          <Callout tone="info">Factors are versioned. Records keep the factor that applied when they were posted.</Callout>
          <Callout tone="ok"><b>FY2025 inventory is ready to file.</b> GHG Protocol Scope 1–3.</Callout>
          <Callout tone="warn"><b>2 records failed a plausibility check.</b> They are excluded from totals until confirmed.</Callout>
          <Callout tone="err"><b>Consumption read as 0 kWh.</b> OCR confidence 41% on register A1.</Callout>
        </div>
      </Sect>

      <Sect title="Stepper, overlays, states">
        <Stepper steps={['Choose site', 'Add data', 'Review & post']} current={1} />
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <Button onClick={() => setDrawer(true)} icon={X.Eye || X.Doc}>Open drawer</Button>
          <Button onClick={() => setModal(true)} icon={X.Plus}>Open modal</Button>
        </div>
        <div className="k2-g2">
          <Card title="Loading">
            <div style={{ display: 'grid', gap: 8 }}>
              <Skeleton h={30} w="55%" /><Skeleton h={20} w="80%" /><Skeleton h={20} w="65%" />
            </div>
          </Card>
          <Card title="Empty">
            <Empty
              icon={X.Search}
              title="No records match these filters"
              body="Try clearing the search or widening the site filter."
              action={<Button size="sm">Clear all filters</Button>}
            />
          </Card>
        </div>
      </Sect>

      {drawer && (
        <Drawer
          title="KESCO · Aug 2025"
          sub={<span className="k2-mono">INV-4192</span>}
          badge={<Badge tone="ok" lg>Posted</Badge>}
          onClose={() => setDrawer(false)}
          footer={<><Button onClick={() => setDrawer(false)}>Close</Button><Button variant="pri">Edit</Button></>}
        >
          <Sect title="Result">
            <div className="k2-g2">
              <Stat label="Emissions" value={fmt(0.39128, 3)} unit="tCO₂e" />
              <Stat label="Activity data" value="292" unit="kWh" />
            </div>
          </Sect>
          <Sect title="Calculation trace">
            <CalcTrace activity={292} activityUnit="kWh" factor={1.34} factorUnit="kgCO₂e/kWh" result={0.39128} />
            <div style={{ marginTop: 12 }}>
              <DList rows={[
                ['Emission factor', 'KEK grid mix (1.34 kgCO₂e/kWh)'],
                ['Factor source', 'World Bank / Grutter Consulting 2016'],
                ['Factor version', 'v1'],
                ['GHG scope', 'Scope 2 — purchased electricity'],
                ['Data quality', <DataQuality q={3} key="dq" />],
              ]} />
            </div>
          </Sect>
        </Drawer>
      )}

      {modal && (
        <Modal
          title="New report"
          sub="Pick a framework — figures are pulled from posted records"
          onClose={() => setModal(false)}
          footer={<>
            <Button onClick={() => setModal(false)}>Cancel</Button>
            <Button variant="mint" icon={X.Check} onClick={() => { setModal(false); toast('Report generating'); }}>Generate</Button>
          </>}
        >
          <Field label="Framework" required>
            <Select><option>GHG Protocol — Corporate Standard</option><option>ESRS E1 — Climate change</option></Select>
          </Field>
          <Callout tone="warn">2 records are under review and will be excluded.</Callout>
        </Modal>
      )}
    </div>
  );
}

export default function UiKitPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <Gallery />
      </Shell>
    </ErrorBoundary>
  );
}
